import {
  useToastController,
  Toast,
  ToastTitle,
  ToastBody,
  Spinner,
} from '@fluentui/react-components';
import type { DuplicateExisting, FileMailItemResult } from '@/features/file-mail/useFileMail';
import { createElement, useCallback } from 'react';
import { APP_TOASTER_ID } from '@/features/notifications/useNotify';

// A single, page-wide toaster id shared by every filing entry point (the
// "Ranger ici" button in FolderRow, the folder context menu, and the
// drag-and-drop handlers). It MUST be a stable constant rather than a
// per-component `useId`, otherwise a component dispatching a toast targets a
// different id than the one mounted <Toaster> uses and nothing ever shows —
// which is why filing appeared to "do nothing".
//
// It is now the app-wide id (see useNotify): filing was simply the first flow to
// need toasts. Kept as an alias so existing imports stay valid.
export const FILE_MAIL_TOASTER_ID = APP_TOASTER_ID;

// Monotonic source for pairing a loading toast with its later result update.
// Module-level so the id survives re-renders of the calling component.
let toastSeq = 0;

/**
 * Where the mail already sits — « « FLUIDES » » or « la racine du projet ».
 *
 * Reads `folderName`, the folder's readable NAME. The server first sent its
 * `path`, which is an ltree of UUIDs (`replace(id::text, '-', '_')`, a machine
 * index for subtree queries), so the toast said "Déjà rangé dans
 * d75f3c31_47d6_46a2_8ada_d42f2d62e214".
 */
function duplicatePlace(existing: DuplicateExisting | undefined): string {
  const name = existing?.folderName?.trim();
  return name ? `« ${name} »` : 'la racine du projet';
}

/** « par Baptiste SCHILDKNECHT le 28/07/2026 14:39 », amputé de ce qu'on ignore. */
function duplicateAuthor(existing: DuplicateExisting | undefined): string | null {
  if (!existing) return null;
  const parts: string[] = [];
  // filedByName only — filedBy is an AAD oid and would print as a raw UUID.
  if (existing.filedByName?.trim()) parts.push(`par ${existing.filedByName.trim()}`);
  const filedAt = formatFiledAt(existing.filedAt);
  if (filedAt) parts.push(`le ${filedAt}`);
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Duplicate clause when the batch ALSO filed or failed something, so the title
 * stays « Rangement terminé » and this line has to carry the verb itself.
 */
function duplicateLine(results: FileMailItemResult[], duplicates: number): string {
  const existing = results.find((r) => r.status === 'duplicate')?.existing;
  if (duplicates > 1) return `${duplicates} déjà rangés dans ${duplicatePlace(existing)}`;
  return `1 déjà rangé dans ${duplicatePlace(existing)}`;
}

// `filedAt` is a server timestamp; a malformed one must not blank the whole
// toast, so an unparseable date simply drops the "le …" clause.
function formatFiledAt(value: string | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function useFileMailToasts() {
  const { dispatchToast, updateToast, dismissToast } = useToastController(FILE_MAIL_TOASTER_ID);

  // Immediately show a persistent "en cours" toast (spinner) the instant filing
  // starts, so the user sees the mail was taken into account while it
  // uploads / converts / stores — instead of a silent gap until the result
  // toast. Returns a toastId to hand back to showResults/showError, which
  // update THIS toast in place (loading → terminé) rather than stacking a
  // second one. timeout: -1 keeps it up until we resolve it.
  const showLoading = useCallback(
    (count = 1): string => {
      const toastId = `file-mail-${toastSeq++}`;
      const label =
        count > 1 ? `Rangement de ${count} mails en cours…` : 'Rangement en cours…';
      dispatchToast(
        createElement(
          Toast,
          null,
          createElement(
            ToastTitle,
            { media: createElement(Spinner, { size: 'tiny' }) },
            label,
          ),
        ),
        { toastId, intent: 'info', timeout: -1 },
      );
      return toastId;
    },
    [dispatchToast],
  );

  // Memoized so callers that depend on these (useDropFiler → useOfficeDragDrop's
  // effect) keep a stable identity across renders. Fresh function identities
  // here made the Office drag-drop effect re-run every render, which could
  // register the handler more than once (double-filing). dispatchToast from
  // useToastController is itself stable for a given toasterId.
  const showResults = useCallback(
    (results: FileMailItemResult[], toastId?: string): void => {
      if (results.length === 0) {
        // Nothing to report — clear any pending loader rather than leave it
        // spinning forever.
        if (toastId) dismissToast(toastId);
        return;
      }
      const created = results.filter((r) => r.status === 'created').length;
      const duplicates = results.filter((r) => r.status === 'duplicate').length;
      const errors = results.filter((r) => r.status === 'error').length;

      // Nothing filed, nothing failed: every mail was ALREADY there. That is a
      // no-op, not a rejection — yet it used to render as « Rangement terminé »
      // under an orange warning triangle, which reads as an error and left the
      // user unsure whether their mail had been lost.
      const onlyDuplicates = duplicates > 0 && created === 0 && errors === 0;
      const onlyErrors = errors > 0 && created === 0 && duplicates === 0;

      // The title now states the outcome instead of merely that the run ended.
      const title = onlyDuplicates
        ? duplicates > 1
          ? `${duplicates} mails déjà rangés`
          : 'Mail déjà rangé'
        : onlyErrors
          ? 'Échec du rangement'
          : 'Rangement terminé';

      // 'warning' is reserved for a genuine problem. A pure duplicate is
      // informational; a partial run where everything filable was filed is a
      // success.
      const intent = errors > 0 ? 'error' : created > 0 ? 'success' : 'info';

      const lines: string[] = [];
      if (onlyDuplicates) {
        // The whole batch targets ONE folder and dedup is scoped to it
        // (`eq('folder_id', resolvedFolderId)` server-side), so every duplicate
        // sits in the same place and naming it once is accurate.
        const existing = results.find((r) => r.status === 'duplicate')?.existing;
        const place = duplicatePlace(existing);
        if (duplicates > 1) {
          lines.push(`Tous dans ${place}, rien à faire`);
        } else {
          const author = duplicateAuthor(existing);
          lines.push(`Dans ${place}${author ? `, ${author}` : ''}`);
        }
      } else {
        if (created > 0) lines.push(`${created} rangé(s)`);
        if (duplicates > 0) lines.push(duplicateLine(results, duplicates));
        if (errors > 0) {
          // Surface the first error message so failures are diagnosable instead
          // of silently swallowed.
          const firstError = results.find((r) => r.status === 'error')?.errorMessage;
          lines.push(`${errors} erreur(s)${firstError ? ` : ${firstError}` : ''}`);
        }
      }

      // Étiquetage « Rangé » raté alors que le rangement a réussi : à dire, mais
      // SANS toucher au titre ni à l'intention. Le rangement — ce que
      // l'utilisateur a demandé — a bien eu lieu ; teinter le toast en erreur
      // pour une pastille manquante ferait croire à un mail perdu.
      //
      // LE MOTIF N'EST DONNÉ QUE POUR UN SEUL MAIL. Au-delà, il serait identique
      // pour les vingt du lot (même boîte, même permission) et n'ajouterait qu'un
      // pavé illisible. Mais sur un mail unique, se taire laissait
      // l'utilisateur — et nous — devant un « ne fonctionne pas » sans le moindre
      // indice ; c'est ce silence qui a rendu la panne indiagnosticable.
      const notCategorized = results.filter((r) => r.categoryError).length;
      if (notCategorized > 0) {
        const only = notCategorized === 1 ? results.find((r) => r.categoryError)?.categoryError : '';
        lines.push(
          notCategorized > 1
            ? `catégorie non appliquée sur ${notCategorized} mails`
            : `catégorie non appliquée${only ? ` : ${only}` : ''}`,
        );
      }

      // Même traitement pour la suppression demandée mais non faite : le
      // rangement a réussi, donc ni le titre ni l'intention ne changent — mais se
      // taire laisserait croire la boîte nettoyée alors que le mail y est encore.
      const notDeleted = results.filter((r) => r.deleteError).length;
      if (notDeleted > 0) {
        const only = notDeleted === 1 ? results.find((r) => r.deleteError)?.deleteError : '';
        lines.push(
          notDeleted > 1
            ? `${notDeleted} mails non supprimés d'Outlook`
            : `mail non supprimé d'Outlook${only ? ` : ${only}` : ''}`,
        );
      }

      const content = createElement(
        Toast,
        null,
        createElement(ToastTitle, null, title),
        createElement(ToastBody, null, lines.join(' • ')),
      );
      const timeout = errors > 0 ? 12000 : 6000;
      // Update the loading toast in place when we have one; otherwise dispatch a
      // fresh toast (keeps the old call sites working unchanged).
      if (toastId) updateToast({ toastId, content, intent, timeout });
      else dispatchToast(content, { intent, timeout });
    },
    [dispatchToast, updateToast, dismissToast],
  );

  const showError = useCallback(
    (message: string, toastId?: string): void => {
      const content = createElement(
        Toast,
        null,
        createElement(ToastTitle, null, 'Échec du rangement'),
        createElement(ToastBody, null, message),
      );
      if (toastId) updateToast({ toastId, content, intent: 'error', timeout: 12000 });
      else dispatchToast(content, { intent: 'error', timeout: 12000 });
    },
    [dispatchToast, updateToast],
  );

  return { toasterId: FILE_MAIL_TOASTER_ID, showLoading, showResults, showError };
}
